<template>
  <div class="panel-card card-lg clearfix">
    <div class="attachments-section">

    <!-- Indent Level -->
    <div class="indent-section" v-if="indentLevelRows.length">
      <div class="section-title mb-10">
        <img :src="packageIcon" width="16" height="16" />
        <span class="section-label ml-5">{{ $t('INDENT_LEVEL') }}</span>
      </div>
      <Table
        :data="indentLevelRows"
        class="table-striped bordered"
        :stripe="true"
      >
        <TableColumn label="DOCUMENT TYPE" width="280">
          <template slot-scope="scope">
            <span class="d-block text-sm">{{ scope.row.label }}</span>
            <span class="text-xs text-grey">{{ scope.row.refId }}</span>
          </template>
        </TableColumn>
        <TableColumn label="DOCUMENTS">
          <template slot-scope="scope">
            <AttachmentsMin
              :attachments="scope.row.docs"
              :disableUpload="true"
              :multiple="true"
              :maxAllowed="scope.row.docs.length"
              @openPreviewSlider="openPreview(scope.row.docs)"
            />
          </template>
        </TableColumn>
      </Table>
    </div>

    <!-- Consignee Level -->
    <div class="consignee-section" v-if="consigneeLevelGroups.length">
      <div class="section-title mb-10">
        <img :src="minitruckIcon" width="16" height="16" />
        <span class="section-label ml-5">{{ $t('INDENT_CONSIGNEE_LEVEL') }}</span>
      </div>
      <Collapse accordion class="collapse-slider" v-model="activeAccordion">
        <CollapseItem
          v-for="(group, gi) in consigneeLevelGroups"
          :key="gi"
          :name="String(gi)"
        >
          <template slot="title">
            <span class="text-sm text-bold">{{ group.name }}</span>
          </template>
          <Table
            :data="group.rows"
            class="table-striped bordered"
            :stripe="true"
          >
            <TableColumn label="DOCUMENT TYPE" width="280">
              <template slot-scope="scope">
                <span class="d-block text-sm">{{ scope.row.label }}</span>
                <span class="text-xs text-grey">{{ scope.row.refId }}</span>
              </template>
            </TableColumn>
            <TableColumn label="DOCUMENTS">
              <template slot-scope="scope">
                <AttachmentsMin
                  :attachments="scope.row.docs"
                  :disableUpload="true"
                  :multiple="true"
                  :maxAllowed="scope.row.docs.length"
                  @openPreviewSlider="openPreview(scope.row.docs)"
                />
              </template>
            </TableColumn>
          </Table>
        </CollapseItem>
      </Collapse>
    </div>

    <!-- Preview Drawer -->
    <Drawer
      :visible.sync="previewVisible"
      direction="rtl"
      size="640px"
      :modal="false"
      :append-to-body="true"
      :withHeader="false"
      class="default-drawer dark"
    >
      <Link :underline="false" class="nav-close" @click="previewVisible = false">
        <svgicon name="cancelwhite" width="12" height="12" :original="true" />
      </Link>
      <div class="drawer-body">
        <AttachmentsPreview
          v-if="previewVisible"
          :attachments="previewDocs"
          :disableUpload="true"
          :disableDelete="true"
          :multiple="true"
          :orderDetails="orderDetails"
          :view-only="true"
        />
      </div>
    </Drawer>
  </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AttachmentsPreview from '@common/components/AttachmentsPreview';
import AttachmentsMin from '@common/components/AttachmentsMin';

export default {
  components: { AttachmentsPreview, AttachmentsMin },
  data() {
    return {
      previewVisible: false,
      previewDocs: [],
      activeAccordion: '0',
      rcDocs: [],
      otherDocs: [],
      inspectionDocsData: [],
      inspectionAtDestDocsData: [],
      packageIcon: require('@common/asset/svg/Package.svg'),
      minitruckIcon: require('@common/asset/svg/Minitruck.svg'),
    };
  },
  async created() {
    if (this.orderDetails && this.orderDetails.order_number) {
      await Promise.all([this.loadRcAttachments(), this.loadOtherDocs(), this.loadInspectionDocs(), this.loadLrAttachmentsIfEmpty()]);
    }
  },
  watch: {
    'orderDetails.order_number': {
      async handler(val) {
        if (val) await Promise.all([this.loadRcAttachments(), this.loadOtherDocs(), this.loadInspectionDocs(), this.loadLrAttachmentsIfEmpty()]);
      },
    },
  },
  computed: {
    ...mapGetters({
      orderDetails: 'order/find',
      configurations: 'app/configurations',
      depots: 'app/otherDepots',
      lrAttachmentsObj: 'order/lrAttachmentsMap',
    }),
    isConsolidatedEnabled() {
      return this.$hasFeature('consolidated_attachment_management');
    },
    // Vehicle inspection attachments (source) — fetched via store
    inspectionDocs() {
      return this.inspectionDocsData;
    },
    // Vehicle inspection attachments (destination) — fetched via store
    inspectionAtDestinationDocs() {
      return this.inspectionAtDestDocsData;
    },
    // Freight charge attachments
    freightDocs() {
      const attachments = this.orderDetails.base_frieght && this.orderDetails.base_frieght.attachments;
      if (!attachments || !attachments.length) return [];
      return attachments.map(a => ({ id: a.id, url: a.url }));
    },
    indentLevelRows() {
      const rows = [];
      const refId = this.orderDetails.order_number || '';
      if (this.rcDocs.length) {
        rows.push({ type: 'rc', label: 'RC Image', refId, docs: this.rcDocs });
      }
      if (this.$hasFeature('truck_inspection') && this.inspectionDocs.length) {
        rows.push({ type: 'inspection', label: 'Vehicle Inspection (Source)', refId, docs: this.inspectionDocs });
      }
      if (this.$hasFeature('truck_inspection_at_destination') && this.inspectionAtDestinationDocs.length) {
        rows.push({ type: 'inspection_dest', label: 'Vehicle Inspection (Destination)', refId, docs: this.inspectionAtDestinationDocs });
      }
      if (this.freightDocs.length) {
        rows.push({ type: 'freight', label: 'Freight Charge', refId, docs: this.freightDocs });
      }
      if (this.otherDocs.length) {
        rows.push({ type: 'other', label: 'Other Documents', refId, docs: this.otherDocs });
      }
      return rows;
    },
    consigneeLevelGroups() {
      if (!this.orderDetails.destinations) return [];
      const DEPOT = 1;
      const seen = new Set();
      const groups = [];

      this.orderDetails.destinations.forEach((dest) => {
        const key = dest.gate_id || dest.location_id;
        if (seen.has(key)) return;
        seen.add(key);

        // Resolve name and refId based on type
        let name;
        let refId;
        if (dest.type === DEPOT && dest.gate_id) {
          const depotDetails = this.depots && this.depots.find(d => d.id === dest.location_id);
          const gateDetails = depotDetails && depotDetails.gates && depotDetails.gates.find(g => g.id === dest.gate_id);
          name = (gateDetails && gateDetails.name) || (dest.details && dest.details.name);
          refId = (gateDetails && gateDetails.reference_id) || (dest.details && dest.details.reference_id);
        } else {
          name = dest.details && dest.details.name;
          refId = dest.details && dest.details.reference_id;
        }

        const rows = [];

        // LR attachments
        const lrDocs = this.lrAttachmentsObj[key] || [];
        if (lrDocs.length) {
          rows.push({ type: 'lr', label: 'LR Attachments', refId, docs: lrDocs });
        }

        // POD attachments — flag ON: pod_attachments bucket, flag OFF: attachments (legacy)
        const podSource = this.isConsolidatedEnabled
          ? (dest.pod_attachments || [])
          : (dest.attachments || []);
        const podDocs = podSource.map(a => ({
          // eslint-disable-next-line no-underscore-dangle
          id: a._id || a.id,
          url: (a.metadata && a.metadata.location) || a.url,
        }));
        if (podDocs.length) {
          rows.push({ type: 'pod', label: 'POD Attachments', refId, docs: podDocs });
        }

        if (rows.length) {
          groups.push({
            name: `${name} - ${refId}`,
            totalDocs: rows.reduce((sum, r) => sum + r.docs.length, 0),
            rows,
          });
        }
      });

      return groups;
    },
  },
  methods: {
    async loadRcAttachments() {
      try {
        const vehicleNumber = this.orderDetails.vehicle && this.orderDetails.vehicle.number;
        if (!vehicleNumber) return;
        const result = await this.$store.dispatch('order/getVehicleNumberList', {
          data: { number: vehicleNumber, is_vehicle_available: true },
        });
        if (result && result.data && result.data.length) {
          this.rcDocs = (result.data[0].rc_attachments || []).map(a => ({
            id: a.id, name: a.name, url: a.url,
          }));
        }
      } catch (e) {
        this.rcDocs = [];
      }
    },
    async loadInspectionDocs() {
      const mapAttachment = a => ({ id: a.id, name: a.name, url: a.url || a.attachment_url });
      try {
        if (this.$hasFeature('truck_inspection')) {
          const inspection = this.orderDetails.flow && this.orderDetails.flow.inspection;
          if (inspection && inspection.attachment_ids && inspection.attachment_ids.length) {
            const ids = inspection.attachment_ids.join(',');
            const result = await this.$store.dispatch('order/attachments', { ids, skipClientId: true });
            this.inspectionDocsData = (result || []).map(mapAttachment);
          }
        }
        if (this.$hasFeature('truck_inspection_at_destination')) {
          const inspectionAtDest = this.orderDetails.flow && this.orderDetails.flow.inspection_at_destination;
          if (inspectionAtDest && inspectionAtDest.attachment_ids && inspectionAtDest.attachment_ids.length) {
            const ids = inspectionAtDest.attachment_ids.join(',');
            const result = await this.$store.dispatch('order/attachments', { ids });
            this.inspectionAtDestDocsData = (result || []).map(mapAttachment);
          }
        }
      } catch (e) {
        this.inspectionDocsData = [];
        this.inspectionAtDestDocsData = [];
      }
    },
    async loadOtherDocs() {
      try {
        const result = await this.$store.dispatch('order/getOtherAttachments', {
          orderNo: this.orderDetails.order_number,
        });
        const docs = Array.isArray(result) ? result : (result && result.data) || [];
        this.otherDocs = docs.map(a => ({ id: a.id, name: a.name, url: a.url }));
      } catch (e) {
        this.otherDocs = [];
      }
    },
    toggleAccordion() {},
    async loadLrAttachmentsIfEmpty() {
      if (Object.keys(this.lrAttachmentsObj).length) return;
      try {
        const result = await this.$store.dispatch('order/getLrAttachments', {
          orderId: this.orderDetails.order_number,
        });
        if (result && result.data && result.data.destinations) {
          const map = {};
          result.data.destinations.forEach((dest) => {
            const key = dest.gate_id || dest.location_id;
            const docs = (dest.lr_attachment_ids || []).map(a => ({
              id: a._id,
              name: a.content_file_name,
              url: (a.metadata && a.metadata.location),
            }));
            if (!map[key]) map[key] = docs;
          });
          this.$store.commit('order/SET_LR_ATTACHMENTS_MAP', map);
        }
      } catch (e) {
        // silent
      }
    },
    openPreview(docs) {
      this.previewDocs = docs;
      this.previewVisible = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.attachments-section {
  padding: 20px;
}

.section-title {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.section-label {
  color: #1a73e8;
  font-weight: 600;
  font-size: 13px;
}

.indent-section {
  margin-bottom: 40px;
}

// Push the table inside accordion away from the blue left border
.consignee-section ::v-deep .el-collapse-item__content {
  padding: 10px 10px 40px 15px;
}
</style>

<style lang="scss">
// Scoped by unique parent class - only affects attachments tab
// el-upload-list__item has overflow:hidden which clips the AttachmentsMin badge
.panel-card .attachments-section .el-table__body-wrapper {
  overflow: visible !important;
}

.panel-card .attachments-section .el-table__body tr td {
  overflow: visible !important;
  padding-top: 10px !important;
}

.panel-card .attachments-section .el-table .cell {
  overflow: visible !important;
}

.panel-card .attachments-section .el-upload-list--picture-card .el-upload-list__item {
  overflow: visible !important;
}
</style>
